import { useState, useEffect } from "react";
import { Slide, ToastContainer, toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { DefaultLayout } from "../../../../layouts/Default";
import { Main } from "../../../../themes/Default/components/Main";
import { Title } from "../../../../themes/Default/components/Title";
import { TopNavigation } from "../../../../themes/Default/components/TopNavigation";
import { Breadcrumps } from "../../../../themes/Default/components/Breadcrumps";
import { Link } from "../../../../themes/Default/components/Breadcrumps/components/Link";
import { Button, ButtonBack } from "../../../../themes/Default/components/TopNavigation/components/Button";
import { List } from "../../../../themes/Default/components/List";
import { Header as ListHeader } from "../../../../themes/Default/components/List/components/Header";
import { Body as ListBody } from "../../../../themes/Default/components/List/components/Body";
import { TableHead } from "../../../../themes/Default/components/List/components/Body/components/TableHead";
import { TableBody } from "../../../../themes/Default/components/List/components/Body/components/TableBody";
import { TableRow } from "../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow";
import { TableHeadCell } from "../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow/components/TableHeadCell";
import { TableDataCell } from "../../../../themes/Default/components/List/components/Body/components/TableBody/components/TableDataCell";
import { Checkbox } from "../../../../themes/Default/components/Form/components/Checkbox";
import { groupsManagement } from "../../../../hooks/api/GroupsManagement";
import { Tabs } from "../../../../themes/Default/components/Tabs";
import { useTranslation } from "react-i18next";
import { Toaster } from "../../../../themes/Default/components/Toaster";
import { useUserToken } from "../../../../hooks/core/UserToken";
import { roles } from "../../../../services/utils/Roles";

const Reports = () => {
    const { t } = useTranslation("common");
    const { groupId } = useParams();
    const navigate = useNavigate();
    const [disableDelete, setDisableDelete] = useState(true);
    const [selectedReports, setSelectedReports] = useState([]);
    const [reports, setReports] = useState();
    const { useReportsListMutation, useDeleteReportMutation, useViewGroupMutation } = groupsManagement;
    const [reportsList, { isLoading }] = useReportsListMutation();
    const [deleteReport] = useDeleteReportMutation();
    const [totalRecords, setTotalRecords] = useState(0);

    const [hasTests, setHasTests] = useState(false);
    const [hasPlan, setHasPlan] = useState(false);
    const [hasProgress, setHasProgress] = useState(false);
    const [viewGroup] = useViewGroupMutation();

    const tabs = [
        { title: t('groups.attendance'),onClick: () => gotoAttendanceTab(groupId)  },
        ...(hasPlan ? [
            { title: t('groups.planning'), onClick: () => gotoPlanningTab(groupId)},
        ] : []),
        ...(hasProgress ? [
            { title: t('groups.progress'), onClick: () => gotoProgressTab(groupId)},
        ] : []),
        ...(hasTests ? [
            { title: t('groups.progressTest'), onClick: () => gotoProgressTestsTab(groupId) }
        ] : []),
        { title: t('reports.title'),isActive: true},
        { title: t('documents'), onClick: () => gotoDocumentsTab(groupId)},
        { title: t('notes'), onClick: () => gotoNotesTab(groupId)},
    ];

    const [{ getDecoded }] = useUserToken();
    const decodedToken = getDecoded();
    if (
        decodedToken?.roles.includes(roles.SUPER_ADMIN) ||
        decodedToken?.roles.includes(roles.SECRETARY) ||
        decodedToken?.roles.includes(roles.DIRECTOR) || 
        decodedToken?.roles.includes(roles.DIRECTOR_OF_STUDIES)
    ) {
        tabs.push({
            title: t("settings.title"),
            onClick: () => gotoEditTab(groupId),
        });
    }

    const gotoPlanningTab = (groupId) => {
        navigate("/groups/" + groupId + "/planning");
    }

    const gotoProgressTestsTab = (groupId) => {
        navigate("/groups/" + groupId + "/tests");
    }

    const gotoProgressTab = (groupId) => {
        navigate("/groups/" + groupId + "/progress");
    }

    const gotoNotesTab = (groupId) => {
        navigate("/groups/" + groupId + "/notes");
    }

    const gotoDocumentsTab = (groupId) => {
        navigate("/groups/" + groupId + "/documents");
    }

    const gotoAttendanceTab = (groupId) => {
        navigate("/groups/" + groupId + "/attendance");
    }

    const gotoEditTab = (groupId) => {
        navigate("/groups/edit/" + groupId);
    }

    const gotoNew = () => {
        navigate(`/groups/${groupId}/reports/add`);
    };

    const gotoEdit = (reportId) => {
        navigate("/groups/" + groupId + "/reports/edit/" + reportId);
    };

    const onSelectionChange = (id) => {
        setSelectedReports((prevSelected) => {
            if (prevSelected.includes(id)) {
                return prevSelected.filter((recordId) => recordId !== id);
            } else {
                return [...prevSelected, id];
            }
        });
    };

    const onDelete = () => {
        toast.warn(
            ({ closeToast }) => (
                <Toaster
                    closeToast={closeToast}
                    description={t("areYouSure")}
                    optionNo={t("no")}
                    optionYes={t("yes")}
                    onClick={() => {
                        closeToast();
                        for (let i = 0; i < selectedReports?.length; i++) {
                            const reportId = selectedReports[i];
                            deleteReport({groupId, reportId }).then((result) => {
                                if (result?.error) {
                                    toast.error(result.error.data.error.responseMessage);
                                } else {
                                    reportsList({groupId}).then((result) => {
                                        setReports(result?.data?.records);
                                        setTotalRecords(result?.data?.totalRecords);
                                    });
                                    setDisableDelete(true);
                                    toast.info(result?.data?.info?.responseMessage);
                                }
                            });
                        }
                    }}
                />
            ),
            {
                autoClose: false,
                progress: false,
                closeButton: false,
            },
        );
    };

    useEffect(() => {
        if (selectedReports.length > 0) {
            setDisableDelete(false);
        } else {
            setDisableDelete(true);
        }
    }, [selectedReports]);

    useEffect(() => {
        viewGroup(groupId).then((result) => {
            setHasTests(result?.data?.hasTests);
            setHasProgress(result?.data?.hasProgress);
            setHasPlan(result?.data?.hasPlan);
        });
        reportsList({groupId}).then((result) => {
            setReports(result?.data?.records);
            setTotalRecords(result?.data?.totalRecords);
        });
    }, [reportsList]);

    return (
        <DefaultLayout>
            
            <TopNavigation>
                <ButtonBack to="/groups" />
                <Button title={t("reports.addNew")} icon="fa-regular fa-plus" onClick={gotoNew}></Button>
                <Button title={t("delete")} icon="fa-regular fa-trash-can" isDisabled={disableDelete} onClick={onDelete}></Button>
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main>
                <Title>{`${t("reports.title")} (${totalRecords})`}</Title>
                <Breadcrumps>
                    <Link title={t("groups.title")} icon="fa-user-group" />
                    <Link title={t("reports.title")} isActive={true} />
                </Breadcrumps>
                {!isLoading && (
                    <>
                        <List>
                            <ListHeader></ListHeader>
                            <ListBody>
                                <TableHead>
                                    <TableRow>
                                        <TableHeadCell></TableHeadCell>
                                        <TableHeadCell></TableHeadCell>
                                        <TableHeadCell>
                                            <span>{t("Naslov")}</span>
                                        </TableHeadCell>
                                        <TableHeadCell>
                                            <span>{t("Verifikovano")}</span>
                                        </TableHeadCell>
                                        <TableHeadCell>
                                            <span>{t("Poslato")}</span>
                                        </TableHeadCell>
                                        <TableHeadCell></TableHeadCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {reports?.length > 0 &&
                                        reports?.map((report, index) => {              
                                            return (
                                                <TableRow key={report.id}>
                                                    <TableDataCell>
                                                            <Button icon="fa-regular fa-pen" onClick={() => gotoEdit(report.id)}></Button>
                                                    </TableDataCell>
                                                    <TableDataCell>
                                                        <Checkbox name="isBlockClass" value={report.id} onChange={() => onSelectionChange(report.id)} />
                                                    </TableDataCell>
                                                    <TableDataCell>{report.reportTypeName}</TableDataCell>
                                                    <TableDataCell>{report.numOfVerified}/{report.numOfAll}</TableDataCell>
                                                    <TableDataCell>{report.numOfSent}/{report.numOfAll}</TableDataCell>
                                                    <TableDataCell></TableDataCell>
                                                </TableRow>
                                            );
                                        })}
                                </TableBody>
                            </ListBody>
                        </List>
                    </>
                )}
            </Main>
        </DefaultLayout>
    );
};

export default Reports;
